import { GroupSettings } from '@moovly/clients/lib/resources/settings/settings.interfaces';
import { clients } from '@moovly/clients';
import { FontObject } from '@moovly/clients/lib/resources/fonts/fonts.interfaces';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  DefaultApiRequestState,
  getDefaultApiRequestState,
  getDefaultReducerForAsyncRequest,
  RequestType,
} from '../../../store/default';
import { mergeDeep } from '../../../utils/object';

export interface FontItem {
  weight: number | null;
  style: string | null;
  font_file: File;
}

export const getSettings = createAsyncThunk('settings/getSettings', async (groupId: string) => {
  return clients.Settings.getGroupSettingsById(groupId).then((settings) => settings);
});

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async ({ groupId, newSettings }: { groupId: string; newSettings: Partial<GroupSettings> }) => {
    return clients.Settings.updateSettings({
      id: groupId,
      type: 'group',
      data: newSettings,
    }).then(() => newSettings);
  }
);

export const getFonts = createAsyncThunk<FontObject[], { externalType: 'organization' | 'group'; externalId: string }>(
  'fonts/getFonts',
  async ({ externalType, externalId }) => {
    if (externalType === 'organization') {
      return clients.Fonts.getOrganizationFamilies(externalId).then((fonts) => fonts.custom.data);
    }
    return clients.Fonts.getGroupFamilies(externalId).then((fonts) => fonts.custom.data);
  }
);

export const deleteFont = createAsyncThunk<
  { fontId: string },
  {
    fontId: string;
    externalType: 'group' | 'organization';
    externalId: string;
  }
>('fonts/deleteFont', async ({ fontId, externalType, externalId }) => {
  await clients.Fonts.deleteFontFamily(fontId, externalId, externalType);
  return { fontId };
});

export const createFont = createAsyncThunk<
  any,
  {
    externalType: 'group' | 'organization';
    externalId: string;
    name: string;
    fonts: FontItem[];
  }
>('fonts/createFont', async ({ externalType, externalId, name, fonts }) => {
  const response = await clients.Fonts.createFontFamily(name, fonts);
  await clients.Fonts.assignFamilies(
    externalType,
    externalId,
    response.map((r: any) => r.id)
  );
  return response;
});

type SettingsState = {
  settings: DefaultApiRequestState<GroupSettings, [RequestType.Get, RequestType.Post, RequestType.Delete]>;
  fonts: DefaultApiRequestState<FontObject[], [RequestType.Get, RequestType.Post, RequestType.Delete]>;
};

const initialState: SettingsState = {
  settings: getDefaultApiRequestState([RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete]),
  fonts: getDefaultApiRequestState([RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete]),
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: {
    //SETTINGS
    ...getDefaultReducerForAsyncRequest({
      thunk: getSettings,
      getInitialState: (state) => state.settings,
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: updateSettings,
      requestType: RequestType.Put,
      getInitialState: (state) => state.settings,
      onSuccess: (state, payload) => {
        mergeDeep(state.data, payload);
      },
    }),
    //FONTS
    ...getDefaultReducerForAsyncRequest({
      thunk: getFonts,
      getInitialState: (state) => state.fonts,
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: deleteFont,
      requestType: RequestType.Delete,
      getInitialState: (state) => state.fonts,
      onSuccess: (state, payload) => {
        state.data = state.data.filter((font: FontObject) => font.id !== payload.fontId);
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: createFont,
      requestType: RequestType.Post,
      getInitialState: (state) => state.fonts,
      onSuccess: (state, payload) => {
        state.data = [...state.data, ...payload];
      },
    }),
  },
});

export default settingsSlice;
